import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Video, VideoOff, Mic, MicOff, Phone, PhoneOff,
  Monitor, MonitorOff, MessageSquare, Send, Users, Clock,
  Calendar, DollarSign, CheckCircle, XCircle, AlertCircle,
  Settings, Maximize2, Minimize2, Volume2, VolumeX,
  Music, Headphones, Radio, Wifi, WifiOff, RefreshCw,
  Copy, ExternalLink, User, Mail, ChevronDown, Camera, 
  SwitchCamera, Sliders, LayoutDashboard
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Audio Level Meter Component
const AudioMeter = ({ stream, label }) => {
  const [level, setLevel] = useState(0);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  
  useEffect(() => {
    if (!stream) return;
    
    const audioTracks = stream.getAudioTracks();
    if (audioTracks.length === 0) return;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      
      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setLevel(Math.min(100, avg * 1.5));
        animationRef.current = requestAnimationFrame(updateLevel);
      };
      
      updateLevel();
      
      return () => {
        cancelAnimationFrame(animationRef.current);
        audioContext.close();
      };
    } catch (e) {
      console.log('Audio meter error:', e);
    }
  }, [stream]);
  
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="text-gray-400">{label}</span>
      <div className="w-20 h-2 bg-gray-700 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-75 ${level > 70 ? 'bg-red-500' : level > 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
          style={{ width: `${level}%` }}
        />
      </div>
    </div>
  );
};

// =============================================================================
// LIVE REMOTE SESSION - WebRTC Real-time Collaboration
// =============================================================================

const LiveRemoteSession = () => {
  const navigate = useNavigate();
  
  // Session Management
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'session'
  
  // WebRTC State
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  
  // Media State
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [remoteVolume, setRemoteVolume] = useState(100);
  const [pushToTalk, setPushToTalk] = useState(false);
  const [isPTTActive, setIsPTTActive] = useState(false);
  
  // Device Selection
  const [availableDevices, setAvailableDevices] = useState({ video: [], audio: [] });
  const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
  const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  
  // UI State
  const [showChat, setShowChat] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  
  // Session Creation
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newSession, setNewSession] = useState({
    client_name: '',
    client_email: '',
    scheduled_date: '',
    scheduled_time: '',
    duration_minutes: 60,
    session_type: 'mixing', // mixing, mastering, production, lesson
    rate_per_hour: 150,
    notes: ''
  });
  
  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const screenStreamRef = useRef(null);
  const chatContainerRef = useRef(null);
  const clientConnectionsRef = useRef({}); // Track connections to multiple clients
  const localStreamRef = useRef(null); // Keep ref to local stream for use in callbacks
  
  // WebRTC Configuration
  const rtcConfig = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' }
    ]
  };
  
  // ==========================================================================
  // Socket.IO Connection for Signaling
  // ==========================================================================
  
  // Using HTTP polling for signaling since WebSockets may not work through proxy
  const pollIntervalRef = useRef(null);
  const lastPollTimeRef = useRef(null);
  
  const startPolling = useCallback((roomCode) => {
    console.log('[Host] Starting polling for room:', roomCode);
    
    // Poll for new clients/signals every 1.5 seconds
    const poll = async () => {
      try {
        const since = lastPollTimeRef.current || '';
        const res = await fetch(`${API}/api/remote-sessions/poll/${roomCode}?role=host&since=${since}`);
        if (res.ok) {
          const data = await res.json();
          lastPollTimeRef.current = data.timestamp;
          
          // Handle new clients
          if (data.new_clients?.length > 0) {
            for (const client of data.new_clients) {
              console.log('[Host] New client detected:', client);
              toast.success(`${client.name} joined the session!`);
              await createPeerConnectionForClient(client.id, client.name, roomCode);
            }
          }
          
          // Handle answers from clients
          if (data.answers?.length > 0) {
            for (const answer of data.answers) {
              console.log('[Host] Received answer from client:', answer.client_id || answer.from_id);
              const clientId = answer.client_id || answer.from_id;
              const pc = clientConnectionsRef.current[clientId]?.pc;
              if (pc && pc.signalingState !== 'stable') {
                try {
                  await pc.setRemoteDescription(new RTCSessionDescription({
                    type: 'answer',
                    sdp: answer.sdp
                  }));
                  console.log('[Host] Set remote description (answer) for client:', clientId);
                } catch (err) {
                  console.error('[Host] Error setting remote description:', err);
                }
              }
            }
          }
          
          // Handle ICE candidates from clients
          if (data.ice_candidates?.length > 0) {
            for (const ice of data.ice_candidates) {
              const clientId = ice.from_id;
              const pc = clientConnectionsRef.current[clientId]?.pc;
              if (pc && ice.candidate) {
                try {
                  await pc.addIceCandidate(new RTCIceCandidate(ice.candidate));
                  console.log('[Host] Added ICE candidate from client:', clientId);
                } catch (err) {
                  console.error('[Host] Error adding ICE candidate:', err);
                }
              }
            }
          }
          
          // Handle chat messages
          if (data.messages?.length > 0) {
            for (const msg of data.messages) {
              setChatMessages(prev => [...prev, {
                id: Date.now() + Math.random(),
                sender: 'remote',
                senderName: msg.sender_name,
                message: msg.message,
                timestamp: msg.timestamp
              }]);
            }
          }
        }
      } catch (err) {
        console.error('[Host] Polling error:', err);
      }
    };
    
    // Initial poll
    poll();
    
    // Set up interval - poll every 1.5 seconds
    pollIntervalRef.current = setInterval(poll, 1500);
    
    // Register as host
    fetch(`${API}/api/remote-sessions/join/${roomCode}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: 'host', name: 'Studio Host' })
    }).then(res => res.json()).then(data => {
      console.log('[Host] Registered as host:', data);
      setIsConnecting(false);
      toast.success('Session started - share room code with client');
    });
  }, []);
  
  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };
  
  const createPeerConnectionForClient = async (clientId, clientName, roomCode) => {
    console.log('[Host] Creating peer connection for client:', clientId, clientName);
    
    // Use ref to get current local stream (avoids stale closure)
    const currentStream = localStreamRef.current;
    console.log('[Host] Local stream available:', !!currentStream);
    console.log('[Host] Local stream tracks:', currentStream?.getTracks().map(t => t.kind) || 'none');
    
    const pc = new RTCPeerConnection(rtcConfig);
    
    // Add local tracks
    if (currentStream) {
      currentStream.getTracks().forEach(track => {
        console.log('[Host] Adding local track:', track.kind);
        pc.addTrack(track, currentStream);
      });
    } else {
      console.warn('[Host] No local stream available! Creating peer connection without local media');
    }
    
    // Handle remote tracks from client
    pc.ontrack = (event) => {
      console.log('[Host] Received track from client:', event.track.kind);
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
      setIsConnected(true);
      toast.success('Client video connected!');
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[Host] Sending ICE candidate to client:', clientId);
        fetch(`${API}/api/remote-sessions/signal/${roomCode}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'ice-candidate',
            from_id: 'host',
            to_id: clientId,
            candidate: event.candidate
          })
        });
      }
    };
    
    pc.onconnectionstatechange = () => {
      console.log('[Host] Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setIsConnected(true);
        toast.success('Video connected!');
      } else if (pc.connectionState === 'failed') {
        toast.error('Connection to client failed');
      }
    };
    
    pc.oniceconnectionstatechange = () => {
      console.log('[Host] ICE connection state:', pc.iceConnectionState);
    };
    
    // Store connection
    clientConnectionsRef.current[clientId] = { pc, name: clientName };
    peerConnectionRef.current = pc;
    
    // Create and send offer
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      console.log('[Host] Created offer, waiting for ICE gathering...');
      
      // Wait for ICE gathering to complete (bundles all candidates into SDP)
      await new Promise((resolve) => {
        if (pc.iceGatheringState === 'complete') {
          resolve();
        } else {
          const checkState = () => {
            console.log('[Host] ICE gathering state:', pc.iceGatheringState);
            if (pc.iceGatheringState === 'complete') {
              pc.removeEventListener('icegatheringstatechange', checkState);
              resolve();
            }
          };
          pc.addEventListener('icegatheringstatechange', checkState);
          // Timeout after 5 seconds
          setTimeout(() => {
            console.log('[Host] ICE gathering timeout, proceeding with partial candidates');
            resolve();
          }, 5000);
        }
      });
      
      // Send offer with all ICE candidates bundled
      const finalOffer = pc.localDescription;
      console.log('[Host] Sending offer with bundled ICE candidates to client:', clientId);
      
      const signalRes = await fetch(`${API}/api/remote-sessions/signal/${roomCode}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'offer',
          from_id: 'host',
          to_id: clientId,
          sdp: finalOffer.sdp
        })
      });
      
      const signalData = await signalRes.json();
      console.log('[Host] Offer sent, response:', signalData);
    } catch (err) {
      console.error('[Host] Error creating/sending offer:', err);
      toast.error('Failed to send connection offer');
    }
  };
  
  // ==========================================================================
  // Data Fetching
  // ==========================================================================
  
  useEffect(() => {
    fetchSessions();
    
    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, []);
  
  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/api/remote-sessions`);
      const data = await res.json();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      // Mock data for development
      setSessions([
        {
          id: 'session_1',
          client_name: 'João Silva',
          client_email: 'joao@example.com',
          scheduled_date: '2026-04-25',
          scheduled_time: '14:00',
          duration_minutes: 90,
          session_type: 'mixing',
          rate_per_hour: 150,
          status: 'scheduled',
          room_code: 'CASA-MIX-7842',
          notes: 'Mixing session for samba album - 8 tracks'
        },
        {
          id: 'session_2',
          client_name: 'Maria Santos',
          client_email: 'maria@example.com',
          scheduled_date: '2026-04-26',
          scheduled_time: '10:00',
          duration_minutes: 60,
          session_type: 'lesson',
          rate_per_hour: 100,
          status: 'scheduled',
          room_code: 'CASA-LES-3291',
          notes: 'Pandeiro lesson - intermediate level'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };
  
  // ==========================================================================
  // Device Enumeration - Get all available cameras and microphones
  // ==========================================================================
  
  const enumerateDevices = async () => {
    try {
      // Request permission first to get device labels
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        .then(stream => stream.getTracks().forEach(track => track.stop()))
        .catch(() => {}); // Ignore errors, just try to get labels
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const videoDevices = devices
        .filter(d => d.kind === 'videoinput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Camera ${d.deviceId.slice(0, 8)}...`
        }));
      
      const audioDevices = devices
        .filter(d => d.kind === 'audioinput')
        .map(d => ({
          deviceId: d.deviceId,
          label: d.label || `Microphone ${d.deviceId.slice(0, 8)}...`
        }));
      
      setAvailableDevices({ video: videoDevices, audio: audioDevices });
      
      // Set defaults if not already set
      if (!selectedVideoDevice && videoDevices.length > 0) {
        setSelectedVideoDevice(videoDevices[0].deviceId);
      }
      if (!selectedAudioDevice && audioDevices.length > 0) {
        setSelectedAudioDevice(audioDevices[0].deviceId);
      }
      
      console.log('Available devices:', { video: videoDevices, audio: audioDevices });
      return { video: videoDevices, audio: audioDevices };
    } catch (err) {
      console.error('Error enumerating devices:', err);
      return { video: [], audio: [] };
    }
  };
  
  // Enumerate devices on mount
  useEffect(() => {
    enumerateDevices();
    
    // Listen for device changes (plugging in/unplugging cameras)
    navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
    };
  }, []);
  
  // ==========================================================================
  // WebRTC Connection Management
  // ==========================================================================
  
  const initializeMedia = async (videoEnabled = true, audioEnabled = true, videoDeviceId = null, audioDeviceId = null) => {
    try {
      // Use selected devices or defaults
      const videoId = videoDeviceId || selectedVideoDevice;
      const audioId = audioDeviceId || selectedAudioDevice;
      
      // Build constraints with specific device IDs and enhanced audio
      const constraints = {
        video: videoEnabled ? { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          ...(videoId ? { deviceId: { exact: videoId } } : {})
        } : false,
        audio: audioEnabled ? { 
          echoCancellation: true, 
          noiseSuppression: true,
          autoGainControl: true,
          ...(audioId ? { deviceId: { exact: audioId } } : {})
        } : false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setLocalStream(stream);
      localStreamRef.current = stream; // Keep ref for use in callbacks
      setIsVideoEnabled(videoEnabled && stream.getVideoTracks().length > 0);
      setIsAudioEnabled(audioEnabled && stream.getAudioTracks().length > 0);
      
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      console.log('[Host] Media initialized:', stream.getTracks().map(t => t.kind));
      
      return stream;
    } catch (err) {
      console.error('Error accessing media devices:', err);
      
      // If video fails, try audio only
      if (videoEnabled && err.name === 'NotAllowedError' || err.name === 'NotFoundError') {
        toast.warning('Camera not available. Starting with audio only...');
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({ 
            video: false, 
            audio: { echoCancellation: true, noiseSuppression: true } 
          });
          setLocalStream(audioStream);
          localStreamRef.current = audioStream;
          setIsVideoEnabled(false);
          setIsAudioEnabled(true);
          return audioStream;
        } catch (audioErr) {
          console.error('Audio also failed:', audioErr);
        }
      }
      
      // If all media fails, allow session without local media (for screen share only)
      toast.warning('No camera/microphone available. You can still screen share and chat.');
      setIsVideoEnabled(false);
      setIsAudioEnabled(false);
      return null;
    }
  };
  
  const createPeerConnection = (stream) => {
    const pc = new RTCPeerConnection(rtcConfig);
    
    // Add local tracks
    stream.getTracks().forEach(track => {
      pc.addTrack(track, stream);
    });
    
    // Handle incoming tracks
    pc.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
      }
    };
    
    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate) {
        // Send candidate to signaling server
        sendSignal({ type: 'ice-candidate', candidate: event.candidate });
      }
    };
    
    // Handle connection state
    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setIsConnected(true);
        setIsConnecting(false);
        toast.success('Connected to remote session!');
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        setIsConnected(false);
        setConnectionError('Connection lost');
      }
    };
    
    // Create data channel for chat/sync
    const dataChannel = pc.createDataChannel('chat', { ordered: true });
    dataChannel.onopen = () => console.log('Data channel open');
    dataChannel.onmessage = handleDataChannelMessage;
    dataChannelRef.current = dataChannel;
    
    // Handle incoming data channels
    pc.ondatachannel = (event) => {
      const channel = event.channel;
      channel.onmessage = handleDataChannelMessage;
      dataChannelRef.current = channel;
    };
    
    peerConnectionRef.current = pc;
    return pc;
  };
  
  const handleDataChannelMessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      if (data.type === 'chat') {
        setChatMessages(prev => [...prev, {
          id: Date.now(),
          sender: 'remote',
          message: data.message,
          timestamp: new Date().toISOString()
        }]);
      } else if (data.type === 'daw-sync') {
        // Handle DAW state synchronization
        console.log('DAW sync received:', data);
      }
    } catch (err) {
      console.error('Error parsing data channel message:', err);
    }
  };
  
  const sendSignal = async (signal) => {
    // In production, this would go through a signaling server (WebSocket)
    // For now, we'll use HTTP polling or a simple signaling endpoint
    try {
      await fetch(`${API}/api/remote-sessions/${activeSession?.id}/signal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signal)
      });
    } catch (err) {
      console.error('Signaling error:', err);
    }
  };
  
  const startSession = async (session) => {
    setIsConnecting(true);
    setConnectionError(null);
    setActiveSession(session);
    setViewMode('session');
    
    try {
      // Initialize media first
      const stream = await initializeMedia();
      console.log('[Host] Media initialized, stream:', stream ? 'yes' : 'no');
      
      // Small delay to ensure view is rendered
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Attach to video element
      if (stream && localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        console.log('[Host] Attached stream to video element in startSession');
      }
      
      // Start HTTP polling for signaling
      startPolling(session.room_code);
      
    } catch (err) {
      setIsConnecting(false);
      setConnectionError(err.message);
      toast.error('Failed to start session');
    }
  };
  
  const endSession = () => {
    // Stop polling
    stopPolling();
    
    // Stop all tracks
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
    }
    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    // Close peer connections
    Object.values(clientConnectionsRef.current).forEach(({ pc }) => {
      if (pc) pc.close();
    });
    clientConnectionsRef.current = {};
    
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
    }
    
    // Reset state
    setLocalStream(null);
    setRemoteStream(null);
    setIsConnected(false);
    setActiveSession(null);
    setViewMode('list');
    setChatMessages([]);
    
    toast.info('Session ended');
  };
  
  // ==========================================================================
  // Media Controls
  // ==========================================================================
  
  // Switch to a different camera
  const switchCamera = async (deviceId) => {
    if (!deviceId) return;
    
    try {
      // Get new video stream from selected camera
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { 
          deviceId: { exact: deviceId },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      const newVideoTrack = newStream.getVideoTracks()[0];
      
      // Replace track in peer connection
      if (peerConnectionRef.current) {
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(newVideoTrack);
        }
      }
      
      // Stop old video track
      if (localStream) {
        localStream.getVideoTracks().forEach(track => track.stop());
      }
      
      // Update local stream with new video track
      if (localStream) {
        // Create new stream with new video and existing audio
        const audioTrack = localStream.getAudioTracks()[0];
        const updatedStream = new MediaStream();
        updatedStream.addTrack(newVideoTrack);
        if (audioTrack) updatedStream.addTrack(audioTrack);
        
        setLocalStream(updatedStream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = updatedStream;
        }
      }
      
      setSelectedVideoDevice(deviceId);
      setIsVideoEnabled(true);
      toast.success('Camera switched');
    } catch (err) {
      console.error('Error switching camera:', err);
      toast.error('Failed to switch camera');
    }
  };
  
  // Switch to a different microphone
  const switchMicrophone = async (deviceId) => {
    if (!deviceId) return;
    
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { 
          deviceId: { exact: deviceId },
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      
      const newAudioTrack = newStream.getAudioTracks()[0];
      
      // Replace track in peer connection
      if (peerConnectionRef.current) {
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'audio');
        if (sender) {
          await sender.replaceTrack(newAudioTrack);
        }
      }
      
      // Stop old audio track
      if (localStream) {
        localStream.getAudioTracks().forEach(track => track.stop());
      }
      
      // Update local stream
      if (localStream) {
        const videoTrack = localStream.getVideoTracks()[0];
        const updatedStream = new MediaStream();
        if (videoTrack) updatedStream.addTrack(videoTrack);
        updatedStream.addTrack(newAudioTrack);
        
        setLocalStream(updatedStream);
      }
      
      setSelectedAudioDevice(deviceId);
      setIsAudioEnabled(true);
      toast.success('Microphone switched');
    } catch (err) {
      console.error('Error switching microphone:', err);
      toast.error('Failed to switch microphone');
    }
  };
  
  const toggleVideo = () => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  };
  
  const toggleAudio = () => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  };
  
  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      // Stop screen sharing
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // Replace with camera
      const videoTrack = localStream?.getVideoTracks()[0];
      if (videoTrack && peerConnectionRef.current) {
        const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          sender.replaceTrack(videoTrack);
        }
      }
      
      setIsScreenSharing(false);
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: { cursor: 'always' },
          audio: true
        });
        
        screenStreamRef.current = screenStream;
        
        // Replace video track with screen share
        const screenTrack = screenStream.getVideoTracks()[0];
        if (peerConnectionRef.current) {
          const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
          if (sender) {
            sender.replaceTrack(screenTrack);
          }
        }
        
        // Handle screen share ending
        screenTrack.onended = () => {
          setIsScreenSharing(false);
          // Restore camera
          const videoTrack = localStream?.getVideoTracks()[0];
          if (videoTrack && peerConnectionRef.current) {
            const sender = peerConnectionRef.current.getSenders().find(s => s.track?.kind === 'video');
            if (sender) {
              sender.replaceTrack(videoTrack);
            }
          }
        };
        
        setIsScreenSharing(true);
        toast.success('Screen sharing started');
      } catch (err) {
        console.error('Screen share error:', err);
        toast.error('Could not start screen sharing');
      }
    }
  };
  
  // ==========================================================================
  // Chat
  // ==========================================================================
  
  const sendChatMessage = () => {
    if (!chatInput.trim()) return;
    
    const message = {
      id: Date.now(),
      sender: 'local',
      message: chatInput.trim(),
      timestamp: new Date().toISOString()
    };
    
    setChatMessages(prev => [...prev, message]);
    
    // Send via HTTP
    if (activeSession) {
      fetch(`${API}/api/remote-sessions/chat/${activeSession.room_code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_name: 'Host',
          message: chatInput.trim(),
          timestamp: new Date().toISOString()
        })
      });
    }
    
    setChatInput('');
  };
  
  useEffect(() => {
    // Auto-scroll chat
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatMessages]);
  
  // Attach local stream to video element when entering session view
  useEffect(() => {
    if (localStream && viewMode === 'session') {
      // Use timeout to ensure DOM element is mounted
      const attachVideo = () => {
        if (localVideoRef.current) {
          console.log('[Host] Attaching local stream to video element');
          localVideoRef.current.srcObject = localStream;
        } else {
          console.log('[Host] localVideoRef not ready, retrying...');
          setTimeout(attachVideo, 100);
        }
      };
      attachVideo();
    }
  }, [localStream, viewMode]);
  
  // Attach remote stream to video element when it becomes available
  useEffect(() => {
    if (remoteStream && remoteVideoRef.current) {
      console.log('[Host] Attaching remote stream to video element');
      console.log('[Host] Remote tracks:', remoteStream.getTracks().map(t => `${t.kind}:${t.enabled}:${t.readyState}`));
      remoteVideoRef.current.srcObject = remoteStream;
      // Ensure NOT muted so we hear the client
      remoteVideoRef.current.muted = false;
      remoteVideoRef.current.volume = remoteVolume / 100;
      // Force play
      remoteVideoRef.current.play().catch(e => console.log('[Host] Remote video play error:', e));
    }
  }, [remoteStream, remoteVolume]);

  // Update remote volume when slider changes
  useEffect(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.volume = remoteVolume / 100;
    }
  }, [remoteVolume]);

  // Push-to-Talk keyboard handler for host
  useEffect(() => {
    if (!pushToTalk || viewMode !== 'session') return;
    
    const handleKeyDown = (e) => {
      if (e.code === 'Space' && !e.repeat && e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setIsPTTActive(true);
        if (localStreamRef.current) {
          const audioTrack = localStreamRef.current.getAudioTracks()[0];
          if (audioTrack) audioTrack.enabled = true;
        }
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setIsPTTActive(false);
        if (localStreamRef.current) {
          const audioTrack = localStreamRef.current.getAudioTracks()[0];
          if (audioTrack) audioTrack.enabled = false;
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [pushToTalk, viewMode]);

  // When PTT mode changes, update audio track
  useEffect(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = pushToTalk ? isPTTActive : isAudioEnabled;
      }
    }
  }, [pushToTalk, isPTTActive, isAudioEnabled]);
  
  // ==========================================================================
  // Session CRUD
  // ==========================================================================
  
  const createSession = async () => {
    try {
      const res = await fetch(`${API}/api/remote-sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newSession,
          room_code: `CASA-${newSession.session_type.toUpperCase().slice(0,3)}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`,
          status: 'scheduled'
        })
      });
      
      if (!res.ok) throw new Error('Failed to create session');
      
      toast.success('Session scheduled!');
      setShowCreateModal(false);
      setNewSession({
        client_name: '',
        client_email: '',
        scheduled_date: '',
        scheduled_time: '',
        duration_minutes: 60,
        session_type: 'mixing',
        rate_per_hour: 150,
        notes: ''
      });
      fetchSessions();
    } catch (err) {
      // For demo, add locally
      const session = {
        id: `session_${Date.now()}`,
        ...newSession,
        room_code: `CASA-${newSession.session_type.toUpperCase().slice(0,3)}-${Math.random().toString(36).substr(2, 4).toUpperCase()}`,
        status: 'scheduled'
      };
      setSessions(prev => [session, ...prev]);
      toast.success('Session scheduled!');
      setShowCreateModal(false);
    }
  };
  
  const copyRoomLink = (roomCode) => {
    const link = `${window.location.origin}/join-session/${roomCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Room link copied to clipboard!');
  };
  
  // ==========================================================================
  // Session Type Badges
  // ==========================================================================
  
  const sessionTypeConfig = {
    mixing: { color: 'blue', label: 'Mixing Session', icon: Music },
    mastering: { color: 'purple', label: 'Mastering', icon: Headphones },
    production: { color: 'green', label: 'Production', icon: Radio },
    lesson: { color: 'amber', label: 'Lesson', icon: User }
  };
  
  // ==========================================================================
  // Render
  // ==========================================================================
  
  if (viewMode === 'session' && activeSession) {
    return (
      <div className={`h-screen bg-black flex flex-col ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        {/* Session Header */}
        <div className="bg-[#1a1a1a] border-b border-[#333] px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={endSession}
              className="p-2 hover:bg-white/10 rounded-lg text-red-400"
            >
              <PhoneOff className="w-5 h-5" />
            </button>
            <div>
              <h2 className="font-bold">{activeSession.client_name}</h2>
              <p className="text-xs text-gray-500">
                {sessionTypeConfig[activeSession.session_type]?.label} • Room: {activeSession.room_code}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {isConnected ? (
              <span className="flex items-center gap-1 text-green-400 text-sm">
                <Wifi className="w-4 h-4" /> Connected
              </span>
            ) : isConnecting ? (
              <span className="flex items-center gap-1 text-amber-400 text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" /> Connecting...
              </span>
            ) : (
              <span className="flex items-center gap-1 text-red-400 text-sm">
                <WifiOff className="w-4 h-4" /> Disconnected
              </span>
            )}
            
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 hover:bg-white/10 rounded-lg"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Video Area */}
          <div className="flex-1 relative bg-[#0a0a0a]">
            {/* Remote Video (Large) - Client's camera */}
            <div 
              className="absolute inset-4 bg-[#1a1a1a] rounded-xl overflow-hidden"
              onClick={() => {
                // Force play on click
                if (remoteVideoRef.current && remoteStream) {
                  remoteVideoRef.current.play().catch(() => {});
                }
              }}
            >
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="w-full h-full object-cover"
              />
              {!remoteStream ? (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <User className="w-16 h-16 mx-auto mb-2 opacity-30" />
                    <p>Waiting for client to join...</p>
                    <p className="text-sm mt-2">Share room code: <span className="text-amber-400 font-mono">{activeSession.room_code}</span></p>
                  </div>
                </div>
              ) : (
                <div className="absolute bottom-2 left-2 bg-green-500 px-2 py-1 rounded text-xs text-black font-bold">
                  CLIENT CONNECTED
                </div>
              )}
            </div>
            
            {/* Local Video (Small PiP) - YOUR camera - TOP RIGHT */}
            <div 
              className="absolute top-8 right-8 w-48 h-36 bg-[#1a1a1a] rounded-lg overflow-hidden border-4 border-amber-500 shadow-2xl"
              style={{ zIndex: 100 }}
            >
              <video
                ref={localVideoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)', backgroundColor: '#000' }}
              />
              {!localStream ? (
                <div className="absolute inset-0 bg-red-900/80 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Video className="w-8 h-8 mx-auto mb-1" />
                    <p className="text-xs font-bold">NO STREAM</p>
                  </div>
                </div>
              ) : !isVideoEnabled ? (
                <div className="absolute inset-0 bg-[#1a1a1a] flex items-center justify-center">
                  <VideoOff className="w-8 h-8 text-gray-500" />
                </div>
              ) : null}
              <div className="absolute bottom-1 left-1 bg-amber-500 px-2 py-0.5 rounded text-xs text-black font-bold">
                YOU
              </div>
            </div>
            
            {/* Media Controls */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
              {/* Audio Level Meters */}
              <div className="flex gap-4 bg-[#1a1a1a]/90 backdrop-blur-sm rounded-full px-4 py-1">
                <AudioMeter stream={localStream} label="Your Mic" />
                <AudioMeter stream={remoteStream} label="Client" />
              </div>
              
              {/* PTT Indicator */}
              {pushToTalk && (
                <div className={`px-4 py-1 rounded-full text-sm ${isPTTActive ? 'bg-green-500 text-black font-bold' : 'bg-[#333] text-gray-400'}`}>
                  {isPTTActive ? '🎤 TRANSMITTING' : 'Hold SPACE to talk'}
                </div>
              )}
              
              <div className="flex items-center gap-2 bg-[#1a1a1a]/90 backdrop-blur-sm rounded-full px-4 py-2">
              {/* PTT or Regular Audio Toggle */}
              {pushToTalk ? (
                <button
                  onMouseDown={() => {
                    setIsPTTActive(true);
                    if (localStreamRef.current) {
                      const track = localStreamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = true;
                    }
                  }}
                  onMouseUp={() => {
                    setIsPTTActive(false);
                    if (localStreamRef.current) {
                      const track = localStreamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = false;
                    }
                  }}
                  onMouseLeave={() => {
                    setIsPTTActive(false);
                    if (localStreamRef.current) {
                      const track = localStreamRef.current.getAudioTracks()[0];
                      if (track) track.enabled = false;
                    }
                  }}
                  className={`p-3 rounded-full transition-all ${isPTTActive ? 'bg-green-500 scale-110' : 'bg-amber-600 hover:bg-amber-500'}`}
                  data-testid="ptt-btn"
                  title="Hold to talk"
                >
                  <Radio className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={toggleAudio}
                  className={`p-3 rounded-full transition-colors ${isAudioEnabled ? 'bg-[#333] hover:bg-[#444]' : 'bg-red-500'}`}
                  data-testid="toggle-audio"
                >
                  {isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                </button>
              )}
              
              <button
                onClick={toggleVideo}
                className={`p-3 rounded-full transition-colors ${isVideoEnabled ? 'bg-[#333] hover:bg-[#444]' : 'bg-red-500'}`}
                data-testid="toggle-video"
              >
                {isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
              </button>
              
              <button
                onClick={toggleScreenShare}
                className={`p-3 rounded-full transition-colors ${isScreenSharing ? 'bg-green-500' : 'bg-[#333] hover:bg-[#444]'}`}
                data-testid="toggle-screen"
              >
                {isScreenSharing ? <Monitor className="w-5 h-5" /> : <MonitorOff className="w-5 h-5" />}
              </button>
              
              {/* Camera/Device Settings */}
              <button
                onClick={() => setShowDeviceSelector(!showDeviceSelector)}
                className={`p-3 rounded-full transition-colors flex items-center gap-1 ${showDeviceSelector ? 'bg-blue-500' : 'bg-[#333] hover:bg-[#444]'}`}
                data-testid="device-settings"
                title="Audio & Device Settings"
              >
                <Settings className="w-5 h-5" />
                <span className="text-xs hidden sm:inline">Settings</span>
              </button>
              
              {/* OPEN DAW BUTTON - Prominent */}
              <button
                onClick={() => navigate(`/admin/studio-session/${activeSession?.room_code || ''}`)}
                className="p-3 rounded-full transition-colors flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-amber-500/30"
                data-testid="open-daw"
                title="Open DAW / Mixer"
              >
                <LayoutDashboard className="w-5 h-5" />
                <span className="text-sm font-bold hidden sm:inline">Open DAW</span>
              </button>
              
              <div className="w-px h-8 bg-[#444] mx-2" />
              
              <button
                onClick={endSession}
                className="p-3 bg-red-500 hover:bg-red-600 rounded-full"
                data-testid="end-session"
              >
                <PhoneOff className="w-5 h-5" />
              </button>
              </div>
            </div>
            
            {/* Device Selector Panel */}
            <AnimatePresence>
              {showDeviceSelector && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-[#1a1a1a] border border-[#333] rounded-xl p-4 shadow-2xl w-80"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium flex items-center gap-2">
                      <Sliders className="w-4 h-4 text-blue-400" /> Device Settings
                    </h3>
                    <button
                      onClick={() => setShowDeviceSelector(false)}
                      className="p-1 hover:bg-white/10 rounded"
                    >
                      <XCircle className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  
                  {/* Camera Selection */}
                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                      <Camera className="w-4 h-4" /> Camera
                    </label>
                    <select
                      value={selectedVideoDevice}
                      onChange={(e) => switchCamera(e.target.value)}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                      data-testid="camera-select"
                    >
                      {availableDevices.video.length === 0 ? (
                        <option value="">No cameras found</option>
                      ) : (
                        availableDevices.video.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="text-[10px] text-gray-500 mt-1">
                      {availableDevices.video.length} camera(s) available
                    </p>
                  </div>
                  
                  {/* Microphone Selection */}
                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                      <Mic className="w-4 h-4" /> Microphone
                    </label>
                    <select
                      value={selectedAudioDevice}
                      onChange={(e) => switchMicrophone(e.target.value)}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                      data-testid="mic-select"
                    >
                      {availableDevices.audio.length === 0 ? (
                        <option value="">No microphones found</option>
                      ) : (
                        availableDevices.audio.map(device => (
                          <option key={device.deviceId} value={device.deviceId}>
                            {device.label}
                          </option>
                        ))
                      )}
                    </select>
                    <p className="text-[10px] text-gray-500 mt-1">
                      {availableDevices.audio.length} microphone(s) available
                    </p>
                  </div>
                  
                  {/* Client Volume */}
                  <div className="mb-4">
                    <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                      <Volume2 className="w-4 h-4" /> Client Volume: {remoteVolume}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={remoteVolume}
                      onChange={(e) => setRemoteVolume(parseInt(e.target.value))}
                      className="w-full accent-blue-500"
                    />
                  </div>
                  
                  {/* Push-to-Talk Toggle */}
                  <div className="mb-4 flex items-center justify-between">
                    <span className="text-sm text-gray-400">Push-to-Talk</span>
                    <button
                      onClick={() => setPushToTalk(!pushToTalk)}
                      className={`px-4 py-1 rounded-lg text-sm font-medium ${pushToTalk ? 'bg-amber-500 text-black' : 'bg-[#333]'}`}
                    >
                      {pushToTalk ? 'ON' : 'OFF'}
                    </button>
                  </div>
                  {pushToTalk && (
                    <p className="text-[10px] text-gray-500 mb-4">
                      Hold SPACE to talk. Your mic is muted by default.
                    </p>
                  )}
                  
                  {/* Refresh Devices */}
                  <button
                    onClick={enumerateDevices}
                    className="w-full py-2 bg-[#333] hover:bg-[#444] rounded-lg text-sm flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" /> Refresh Devices
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Chat Sidebar */}
          {showChat && (
            <div className="w-80 bg-[#1a1a1a] border-l border-[#333] flex flex-col">
              <div className="p-3 border-b border-[#333] flex items-center justify-between">
                <h3 className="font-medium flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" /> Session Chat
                </h3>
                <button onClick={() => setShowChat(false)} className="p-1 hover:bg-white/10 rounded">
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
              
              {/* Messages */}
              <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-3 space-y-3">
                {chatMessages.length === 0 && (
                  <p className="text-center text-gray-500 text-sm py-8">No messages yet</p>
                )}
                {chatMessages.map(msg => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender === 'local' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                      msg.sender === 'local' ? 'bg-blue-500' : 'bg-[#333]'
                    }`}>
                      <p className="text-sm">{msg.message}</p>
                      <p className="text-[10px] text-white/50 mt-1">
                        {new Date(msg.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Input */}
              <div className="p-3 border-t border-[#333]">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    placeholder="Type a message..."
                    className="flex-1 bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
                  />
                  <button
                    onClick={sendChatMessage}
                    className="p-2 bg-blue-500 hover:bg-blue-600 rounded-lg"
                    data-testid="send-chat"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Show Chat Toggle */}
          {!showChat && (
            <button
              onClick={() => setShowChat(true)}
              className="absolute top-20 right-4 p-2 bg-[#1a1a1a] border border-[#333] rounded-lg hover:bg-[#252525]"
            >
              <MessageSquare className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    );
  }
  
  // ==========================================================================
  // Session List View
  // ==========================================================================
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="bg-card border-b border-border/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-white/10 rounded-lg">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Video className="w-6 h-6 text-blue-400" />
                Live Remote Sessions
              </h1>
              <p className="text-sm text-muted-foreground">
                Real-time collaboration with clients via video call
              </p>
            </div>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg font-medium flex items-center gap-2"
            data-testid="schedule-session"
          >
            <Calendar className="w-4 h-4" /> Schedule Session
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sessions.filter(s => s.status === 'scheduled').length}</p>
                <p className="text-xs text-muted-foreground">Scheduled</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sessions.filter(s => s.status === 'completed').length}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {sessions.reduce((acc, s) => acc + (s.duration_minutes || 0), 0)}
                </p>
                <p className="text-xs text-muted-foreground">Total Minutes</p>
              </div>
            </div>
          </div>
          
          <div className="bg-card border border-border/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <DollarSign className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  ${sessions.filter(s => s.status === 'completed').reduce((acc, s) => acc + ((s.rate_per_hour || 0) * (s.duration_minutes || 0) / 60), 0).toFixed(0)}
                </p>
                <p className="text-xs text-muted-foreground">Revenue</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Sessions List */}
        <div className="bg-card border border-border/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-border/50">
            <h2 className="font-bold">Upcoming Sessions</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading sessions...</div>
          ) : sessions.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No sessions scheduled</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-sm"
              >
                Schedule Your First Session
              </button>
            </div>
          ) : (
            <div className="divide-y divide-border/50">
              {sessions.map(session => {
                const config = sessionTypeConfig[session.session_type] || sessionTypeConfig.mixing;
                const TypeIcon = config.icon;
                
                return (
                  <div
                    key={session.id}
                    className="p-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 bg-${config.color}-500/20 rounded-lg`}>
                          <TypeIcon className={`w-5 h-5 text-${config.color}-400`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold">{session.client_name}</h3>
                            <span className={`px-2 py-0.5 text-xs rounded-full bg-${config.color}-500/20 text-${config.color}-400`}>
                              {config.label}
                            </span>
                            {session.status === 'in_progress' && (
                              <span className="px-2 py-0.5 text-xs rounded-full bg-green-500 text-white animate-pulse">
                                LIVE
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {session.scheduled_date} at {session.scheduled_time}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {session.duration_minutes} min
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              ${session.rate_per_hour}/hr
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => copyRoomLink(session.room_code)}
                          className="px-3 py-1.5 bg-[#333] hover:bg-[#444] rounded-lg text-sm flex items-center gap-1"
                          title="Copy invite link"
                        >
                          <Copy className="w-3 h-3" />
                          {session.room_code}
                        </button>
                        
                        <button
                          onClick={() => startSession(session)}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg font-medium flex items-center gap-2"
                          data-testid={`start-session-${session.id}`}
                        >
                          <Video className="w-4 h-4" /> Start
                        </button>
                      </div>
                    </div>
                    
                    {session.notes && (
                      <p className="mt-2 text-sm text-muted-foreground bg-[#0a0a0a] rounded-lg px-3 py-2 ml-16">
                        {session.notes}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      
      {/* Create Session Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-[#1a1a1a] rounded-xl w-full max-w-lg border border-[#333]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 border-b border-[#333] flex items-center justify-between">
                <h2 className="text-lg font-bold">Schedule Remote Session</h2>
                <button onClick={() => setShowCreateModal(false)} className="p-1 hover:bg-white/10 rounded">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Client Name</label>
                    <input
                      type="text"
                      value={newSession.client_name}
                      onChange={(e) => setNewSession(prev => ({ ...prev, client_name: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Client Email</label>
                    <input
                      type="email"
                      value={newSession.client_email}
                      onChange={(e) => setNewSession(prev => ({ ...prev, client_email: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Date</label>
                    <input
                      type="date"
                      value={newSession.scheduled_date}
                      onChange={(e) => setNewSession(prev => ({ ...prev, scheduled_date: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Time</label>
                    <input
                      type="time"
                      value={newSession.scheduled_time}
                      onChange={(e) => setNewSession(prev => ({ ...prev, scheduled_time: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Session Type</label>
                    <select
                      value={newSession.session_type}
                      onChange={(e) => setNewSession(prev => ({ ...prev, session_type: e.target.value }))}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                    >
                      <option value="mixing">Mixing</option>
                      <option value="mastering">Mastering</option>
                      <option value="production">Production</option>
                      <option value="lesson">Lesson</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Duration (min)</label>
                    <input
                      type="number"
                      value={newSession.duration_minutes}
                      onChange={(e) => setNewSession(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 60 }))}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                      min="15"
                      step="15"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Rate ($/hr)</label>
                    <input
                      type="number"
                      value={newSession.rate_per_hour}
                      onChange={(e) => setNewSession(prev => ({ ...prev, rate_per_hour: parseInt(e.target.value) || 0 }))}
                      className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                      min="0"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Notes</label>
                  <textarea
                    value={newSession.notes}
                    onChange={(e) => setNewSession(prev => ({ ...prev, notes: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-[#333] rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 h-20 resize-none"
                    placeholder="Session details, requirements, etc."
                  />
                </div>
              </div>
              
              <div className="p-4 border-t border-[#333] flex justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-[#333] hover:bg-[#444] rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={createSession}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg font-medium flex items-center gap-2"
                >
                  <Calendar className="w-4 h-4" /> Schedule Session
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LiveRemoteSession;
